import { intersection, map, toString } from "lodash/fp";

import type { SelectChangeEvent } from "@mui/material";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";

import SettingRadio from "@app/analytics/account/components/SettingRadio";
import { getSettingDisabledOpts } from "@app/analytics/account/UserPreferences/utils";
import type { User, UserSettings } from "@app/common/types";
import * as UserUtils from "@app/common/utils/userUtils";

import { Column } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

const ALL_MEETING_TYPE_VALUES = ["true", "false"];

const MEETING_TYPE_OPTIONS = [
  {
    value: "false",
    label: () => "Join internal and external meetings",
  },
  {
    value: "true",
    label: (domain: string) =>
      `Join internal meetings only (all participants are @${domain})`,
  },
];

const getEnabledValues = (editOptions?: boolean[]) => {
  if (!editOptions) return ALL_MEETING_TYPE_VALUES;
  return intersection(ALL_MEETING_TYPE_VALUES, map(toString, editOptions));
};

const MeetingTypeSelect = ({
  onChange,
  user,
}: {
  onChange: (settings: Partial<UserSettings>) => void;
  user: User;
}) => {
  const enabledValues = getEnabledValues(
    user?.editOptions?.calendarJoinInternalOnly
  );
  const selected = `${Boolean(user?.calendarOnlyJoinInternal)}`;

  const handleChange = (e: SelectChangeEvent) => {
    const calendarOnlyJoinInternal = e.target.value === "true";
    onChange({ calendarOnlyJoinInternal });
  };

  const userDomain = UserUtils.getDomainFromEmail(user.email);

  return (
    <Column sx={{ color: "shades.neutral.800" }} spacing={1}>
      <Typography variant="bodyPrimaryMed">Meeting Type</Typography>
      <Column ml={0}>
        <RadioGroup onChange={handleChange}>
          {MEETING_TYPE_OPTIONS.map(({ value, label }) => {
            const { disabled: optionIsDisabled, tooltip } =
              getSettingDisabledOpts(true, enabledValues.includes(value));

            return (
              <TooltipWrapper
                key={value}
                show={optionIsDisabled}
                title={tooltip}
              >
                <SettingRadio
                  value={value}
                  checked={value === selected}
                  disabled={optionIsDisabled}
                  label={label(userDomain)}
                />
              </TooltipWrapper>
            );
          })}
        </RadioGroup>
      </Column>
    </Column>
  );
};

export default MeetingTypeSelect;
